import Select from "react-select";
import ReactQuill from "react-quill";

/* USER DEFINED COMPONENTS */
import InputField from "../../../components/input";

/* Lucide icon names supported by the website's service cards */
export const ICON_OPTIONS = [
  "Pill",
  "FlaskConical",
  "Syringe",
  "ArrowRightLeft",
  "Truck",
  "ClipboardCheck",
  "ShieldCheck",
  "CalendarClock",
  "RefreshCw",
  "PackageCheck",
  "Stethoscope",
  "Cross",
  "Footprints",
  "Leaf",
  "Droplet",
  "Candy",
  "HeartHandshake",
  "MessagesSquare",
].map((name) => ({ value: name, label: name }));

export const STATUS_OPTIONS = [
  { value: true, label: "Active (visible on website)" },
  { value: false, label: "Hidden" },
];

const quillModules = {
  toolbar: [
    [{ header: [2, 3, 4, false] }],
    ["bold", "italic", "underline", "strike"],
    [{ list: "ordered" }, { list: "bullet" }],
    ["blockquote"],
    ["link"],
    ["clean"],
  ],
};

const quillFormats = [
  "header",
  "bold",
  "italic",
  "underline",
  "strike",
  "list",
  "bullet",
  "blockquote",
  "link",
];

/* Shared fields for the add/edit service pages. Expects a formik instance. */
const ServiceForm = ({ formik }) => {
  return (
    <div className="grid grid-cols-1 xl:grid-cols-3 lg:grid-cols-3 gap-y-5 xl:gap-5 lg:gap-5">
      <div className="col-span-2">
        <div className="bg-white rounded-lg border border-authBorder">
          <div className="px-5 py-3 border-b border-authBorder">
            <h5 className="text-lg font-ibm-bold text-black">
              Service Details
            </h5>
            <p className="text-sm font-ibm-regular text-greyText">
              Add the title, short description, and full description for this
              service.
            </p>
          </div>
          <div className="p-5">
            <div className="grid grid-cols-1 gap-5">
              <div className="col-span-1">
                <div className="form-group">
                  <label className="text-sm font-ibm-regular text-greyText">
                    Service Title <span className="text-red-500">*</span>
                  </label>
                  <div className="mt-1">
                    <InputField
                      type="text"
                      name="title"
                      id="title"
                      value={formik.values.title}
                      onChange={formik.handleChange}
                      error={
                        formik.errors.title && formik.touched.title
                          ? true
                          : false
                      }
                      placeholder="Write the service title"
                    />
                  </div>
                  {formik.errors.title && formik.touched.title && (
                    <div className="text-red-500 text-xs mt-1">
                      {formik.errors.title}
                    </div>
                  )}
                </div>
              </div>

              <div className="col-span-1">
                <div className="form-group">
                  <label className="text-sm font-ibm-regular text-greyText">
                    Short Description <span className="text-red-500">*</span>
                  </label>
                  <p className="text-xs font-ibm-regular text-greyText">
                    Shown on the service cards on the website.
                  </p>
                  <div className="mt-1">
                    <textarea
                      name="shortDescription"
                      id="shortDescription"
                      value={formik.values.shortDescription}
                      onChange={formik.handleChange}
                      className={`w-full px-5 py-3 border ${
                        formik.errors.shortDescription &&
                        formik.touched.shortDescription
                          ? "border-red-500"
                          : "border-authBorder"
                      } rounded-md text-base font-ibm-regular text-black focus:outline-logoYellow min-h-[10vh]`}
                      placeholder="One or two sentences shown on the service card"
                    />
                  </div>
                  {formik.errors.shortDescription &&
                    formik.touched.shortDescription && (
                      <div className="text-red-500 text-xs mt-1">
                        {formik.errors.shortDescription}
                      </div>
                    )}
                </div>
              </div>

              <div className="col-span-1">
                <div className="form-group">
                  <label className="text-sm font-ibm-regular text-greyText">
                    Service Description <span className="text-red-500">*</span>
                  </label>
                  <div className="mt-1 blog-editor">
                    <ReactQuill
                      theme="snow"
                      value={formik.values.description}
                      onChange={(html) =>
                        formik.setFieldValue("description", html)
                      }
                      modules={quillModules}
                      formats={quillFormats}
                      placeholder="Write the full service description here"
                    />
                  </div>
                  {formik.errors.description && formik.touched.description && (
                    <div className="text-red-500 text-xs mt-1">
                      {formik.errors.description}
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* SEO DETAILS SECTION */}
        <div className="bg-white rounded-lg border border-authBorder mt-5">
          <div className="px-5 py-3 border-b border-authBorder">
            <h5 className="text-lg font-ibm-bold text-black">
              SEO Details (Optional)
            </h5>
            <p className="text-sm font-ibm-regular text-greyText">
              Add a custom slug and meta tags for search engines.
            </p>
          </div>
          <div className="p-5">
            <div className="grid grid-cols-1 xl:grid-cols-2 lg:grid-cols-2 gap-5">
              <div className="col-span-1">
                <div className="form-group">
                  <label className="text-sm font-ibm-regular text-greyText">
                    Slug
                  </label>
                  <div className="mt-1">
                    <InputField
                      type="text"
                      name="slug"
                      id="slug"
                      value={formik.values.slug}
                      onChange={formik.handleChange}
                      placeholder="e.g., free-delivery (left blank = from title)"
                    />
                  </div>
                </div>
              </div>
              <div className="col-span-1">
                <div className="form-group">
                  <label className="text-sm font-ibm-regular text-greyText">
                    SEO Title
                  </label>
                  <div className="mt-1">
                    <InputField
                      type="text"
                      name="seoTitle"
                      id="seoTitle"
                      value={formik.values.seoTitle}
                      onChange={formik.handleChange}
                      placeholder="SEO Meta Title"
                    />
                  </div>
                </div>
              </div>
              <div className="col-span-2">
                <div className="form-group">
                  <label className="text-sm font-ibm-regular text-greyText">
                    SEO Description
                  </label>
                  <div className="mt-1">
                    <textarea
                      name="seoDescription"
                      id="seoDescription"
                      value={formik.values.seoDescription}
                      onChange={formik.handleChange}
                      className="w-full px-4 py-2 border border-authBorder rounded-md focus:outline-none focus:border-logoYellow transition-colors duration-300 min-h-[100px]"
                      placeholder="SEO Meta Description"
                    />
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      <div className="col-span-1">
        {/* DISPLAY SECTION */}
        <div className="bg-white rounded-lg border border-authBorder">
          <div className="px-5 py-3 border-b border-authBorder">
            <h5 className="text-lg font-ibm-bold text-black">Display</h5>
            <p className="text-sm font-ibm-regular text-greyText">
              Control how this service appears on the website.
            </p>
          </div>
          <div className="p-5">
            <div className="form-group">
              <label className="text-sm font-ibm-regular text-greyText">
                Status
              </label>
              <div className="relative mt-1">
                <Select
                  options={STATUS_OPTIONS}
                  placeholder="Select status"
                  classNamePrefix="dropdown"
                  name="isActive"
                  id="isActive"
                  value={STATUS_OPTIONS.find(
                    (option) => option.value === formik.values.isActive
                  )}
                  onChange={(selectedOption) =>
                    formik.setFieldValue("isActive", selectedOption?.value)
                  }
                />
              </div>
            </div>

            <div className="form-group mt-4">
              <label className="text-sm font-ibm-regular text-greyText">
                Card Icon
              </label>
              <div className="relative mt-1">
                <Select
                  options={ICON_OPTIONS}
                  placeholder="Select icon"
                  classNamePrefix="dropdown"
                  name="icon"
                  id="icon"
                  value={ICON_OPTIONS.find(
                    (option) => option.value === formik.values.icon
                  )}
                  onChange={(selectedOption) =>
                    formik.setFieldValue("icon", selectedOption?.value)
                  }
                />
              </div>
            </div>

            <div className="form-group mt-4">
              <label className="text-sm font-ibm-regular text-greyText">
                Display Order
              </label>
              <p className="text-xs font-ibm-regular text-greyText">
                Lower numbers appear first on the services page.
              </p>
              <div className="mt-1">
                <InputField
                  type="number"
                  name="sortOrder"
                  id="sortOrder"
                  value={formik.values.sortOrder}
                  onChange={formik.handleChange}
                  placeholder="e.g., 1"
                />
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ServiceForm;
